import { useEffect, useState } from 'react';
import api from '../api/apiClient';

/**
 * Role-wise menu access (V30). The server decides which sub-menus a login's role has and which
 * actions it may use on each (`GET /api/my/access`); the sidebar, the route guards and in-page
 * buttons all read that one answer from here.
 *
 * It is loaded once per session (after login, or on the first guarded page after a reload) and
 * kept in memory and localStorage, so guards can answer synchronously. `clearAccess` drops it on
 * logout; `loadAccess(true)` refreshes it (the roles screen does so after an admin edits grants).
 */

export type MenuAction = 'VIEW' | 'CREATE' | 'EDIT' | 'DELETE' | 'APPROVE' | 'EXPORT';

export interface MenuEntry {
  code: string;
  moduleCode: string;
  moduleLabel: string;
  moduleIcon: string;
  moduleSort: number;
  label: string;
  path: string;
  icon: string;
  sortOrder: number;
  /** For the signed-in user: the granted actions (VIEW first). In the catalogue: the supported ones. */
  actions: MenuAction[];
  adminOnly: boolean;
  description: string | null;
}

export interface Access {
  role: string | null;
  roleLabel: string | null;
  admin: boolean;
  menus: MenuEntry[];
}

const STORAGE_KEY = 'menuAccess';

let current: Access | null = read();
let inflight: Promise<Access> | null = null;
const listeners = new Set<() => void>();

function read(): Access | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Access) : null;
  } catch {
    return null;
  }
}

function publish(next: Access | null) {
  current = next;
  try {
    if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable: memory only */
  }
  listeners.forEach((l) => l());
}

/** Fetches the user's menu (once; `force` refetches). */
export function loadAccess(force = false): Promise<Access> {
  if (current && !force) return Promise.resolve(current);
  if (!inflight) {
    inflight = api
      .get('/api/my/access')
      .then((r) => {
        const a = r.data.data as Access;
        publish(a);
        return a;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

export function clearAccess(): void {
  publish(null);
}

export function getAccess(): Access | null {
  return current;
}

/** True when the user has the sub-menu and, beyond VIEW, the action on it. Admin has everything. */
export function hasMenu(code: string, action: MenuAction = 'VIEW'): boolean {
  if (!current) return false;
  if (current.admin) return true;
  const m = current.menus.find((x) => x.code === code);
  return !!m && m.actions.includes(action);
}

/** True when the user is admin (for the few things that stay with admin, e.g. company edits). */
export function isAdmin(): boolean {
  return !!current?.admin;
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/** The access answer, re-rendering when it changes. */
export function useAccess(): Access | null {
  const [value, setValue] = useState<Access | null>(current);
  useEffect(() => {
    setValue(current);
    return subscribe(() => setValue(current));
  }, []);
  return value;
}

/** `hasMenu` as a hook, so a button re-renders when access loads or changes. */
export function useHasMenu(code: string, action: MenuAction = 'VIEW'): boolean {
  useAccess();
  return hasMenu(code, action);
}

/**
 * Quotation rights (V36), as the API enforces them by role: Accounts and the admin raise
 * quotations and add the client's work order; only the admin approves one.
 */
export function useQuotationRights(): { canRaise: boolean; canApprove: boolean } {
  const access = useAccess();
  const role = String(access?.role ?? '').trim().toLowerCase();
  const admin = Boolean(access?.admin) || role === 'admin';
  return { canRaise: admin || role === 'accounts', canApprove: admin };
}
